/**
 * Travel Posts API Service
 * 处理旅行帖子的增删改查操作
 */

import { supabase } from './supabase';

export type TravelPostSourceType = 'manual' | 'itinerary_snapshot';

export interface CreatePostPayload {
  title: string;
  summary?: string;
  cover_image?: string;
  city?: string;
  tags?: string[];
  content_json: any;
  content_html?: string;
  status?: 'draft' | 'published';
  source_type?: TravelPostSourceType;
  source_itinerary_id?: string;
  snapshot_schema_version?: string;
  snapshot_generated_at?: string;
  snapshot_meta?: Record<string, any>;
}

export interface UpdatePostPayload {
  title?: string;
  summary?: string;
  cover_image?: string;
  city?: string;
  tags?: string[];
  content_json?: any;
  content_html?: string;
  status?: 'draft' | 'published';
  source_type?: TravelPostSourceType;
  source_itinerary_id?: string | null;
  snapshot_schema_version?: string | null;
  snapshot_generated_at?: string | null;
  snapshot_meta?: Record<string, any> | null;
}

export interface TravelPost {
  id: string;
  author_id: string;
  title: string;
  summary?: string;
  cover_image?: string;
  city?: string;
  tags?: string[];
  content_json: any;
  content_html?: string;
  view_count: number;
  like_count: number;
  comment_count: number;
  status: 'draft' | 'published';
  source_type: TravelPostSourceType;
  source_itinerary_id?: string | null;
  snapshot_schema_version?: string | null;
  snapshot_generated_at?: string | null;
  snapshot_meta?: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

/**
 * 创建旅行帖子
 */
export async function createTravelPost(payload: CreatePostPayload): Promise<TravelPost> {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('用户未认证');
  }

  const insertPayload: Record<string, any> = {
    author_id: user.id,
    title: payload.title,
    summary: payload.summary,
    cover_image: payload.cover_image,
    city: payload.city,
    tags: payload.tags || [],
    content_json: payload.content_json,
    content_html: payload.content_html,
    status: payload.status || 'draft',
    source_type: payload.source_type || 'manual',
  };

  if (payload.source_itinerary_id) {
    insertPayload.source_itinerary_id = payload.source_itinerary_id;
  }
  if (payload.snapshot_schema_version) {
    insertPayload.snapshot_schema_version = payload.snapshot_schema_version;
  }
  if (payload.snapshot_generated_at) {
    insertPayload.snapshot_generated_at = payload.snapshot_generated_at;
  }
  if (payload.snapshot_meta) {
    insertPayload.snapshot_meta = payload.snapshot_meta;
  }

  const { data, error } = await supabase
    .from('travel_posts')
    .insert([insertPayload])
    .select()
    .single();

  if (error) {
    console.error('创建帖子失败:', error);
    throw error;
  }

  return data as TravelPost;
}

/**
 * 获取单个旅行帖子
 */
export async function getTravelPost(postId: string): Promise<TravelPost> {
  const { data, error } = await supabase
    .from('travel_posts')
    .select('*')
    .eq('id', postId)
    .single();

  if (error) {
    console.error('获取帖子失败:', error);
    throw error;
  }

  return data as TravelPost;
}

/**
 * 获取旅行帖子列表（支持过滤、排序、分页）
 */
export async function getTravelPosts(options?: {
  status?: 'draft' | 'published';
  city?: string;
  tags?: string[];
  sortBy?: 'created_at' | 'view_count' | 'like_count';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}): Promise<{ data: TravelPost[]; count: number }> {
  let query = supabase.from('travel_posts').select('*', { count: 'exact' });

  // 默认只查询已发布的帖子
  if (options?.status) {
    query = query.eq('status', options.status);
  } else {
    query = query.eq('status', 'published');
  }

  // 按城市过滤
  if (options?.city) {
    query = query.eq('city', options.city);
  }

  // 排序
  const sortBy = options?.sortBy || 'created_at';
  const sortOrder = options?.sortOrder || 'desc';
  query = query.order(sortBy, { ascending: sortOrder === 'asc' });

  // 分页
  const page = options?.page || 1;
  const pageSize = options?.pageSize || 20;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('获取帖子列表失败:', error);
    throw error;
  }

  return { data: (data || []) as TravelPost[], count: count || 0 };
}

/**
 * 获取用户的旅行帖子
 */
export async function getUserTravelPosts(options?: {
  sortBy?: 'created_at' | 'view_count' | 'like_count';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}): Promise<{ data: TravelPost[]; count: number }> {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('用户未认证');
  }

  let query = supabase
    .from('travel_posts')
    .select('*', { count: 'exact' })
    .eq('author_id', user.id);

  // 排序
  const sortBy = options?.sortBy || 'created_at';
  const sortOrder = options?.sortOrder || 'desc';
  query = query.order(sortBy, { ascending: sortOrder === 'asc' });

  // 分页
  const page = options?.page || 1;
  const pageSize = options?.pageSize || 20;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('获取用户帖子失败:', error);
    throw error;
  }

  return { data: (data || []) as TravelPost[], count: count || 0 };
}

/**
 * 更新旅行帖子
 */
export async function updateTravelPost(
  postId: string,
  payload: UpdatePostPayload
): Promise<TravelPost> {
  const { data, error } = await supabase
    .from('travel_posts')
    .update(payload)
    .eq('id', postId)
    .select()
    .single();

  if (error) {
    console.error('更新帖子失败:', error);
    throw error;
  }

  return data as TravelPost;
}

/**
 * 发布旅行帖子（从草稿变为已发布）
 */
export async function publishTravelPost(postId: string): Promise<TravelPost> {
  return updateTravelPost(postId, { status: 'published' });
}

/**
 * 删除旅行帖子
 */
export async function deleteTravelPost(postId: string): Promise<void> {
  const { error } = await supabase
    .from('travel_posts')
    .delete()
    .eq('id', postId);

  if (error) {
    console.error('删除帖子失败:', error);
    throw error;
  }
}

/**
 * 增加帖子浏览次数
 */
export async function incrementPostViewCount(postId: string): Promise<void> {
  const { error } = await supabase.rpc('increment_post_view', {
    post_id: postId,
  });

  if (error) {
    // 如果 RPC 不存在，使用直接更新
    const post = await getTravelPost(postId);
    await updateTravelPost(postId, {
      view_count: (post.view_count || 0) + 1,
    });
  }
}

/**
 * 搜索旅行帖子
 */
export async function searchTravelPosts(
  query: string,
  options?: {
    page?: number;
    pageSize?: number;
  }
): Promise<{ data: TravelPost[]; count: number }> {
  let dbQuery = supabase
    .from('travel_posts')
    .select('*', { count: 'exact' })
    .eq('status', 'published')
    .or(`title.ilike.%${query}%,summary.ilike.%${query}%,city.ilike.%${query}%`);

  // 分页
  const page = options?.page || 1;
  const pageSize = options?.pageSize || 20;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  dbQuery = dbQuery.range(from, to);

  const { data, error, count } = await dbQuery;

  if (error) {
    console.error('搜索帖子失败:', error);
    throw error;
  }

  return { data: (data || []) as TravelPost[], count: count || 0 };
}

export default {
  createTravelPost,
  getTravelPost,
  getTravelPosts,
  getUserTravelPosts,
  updateTravelPost,
  publishTravelPost,
  deleteTravelPost,
  incrementPostViewCount,
  searchTravelPosts,
};
